import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ERROR_MESSAGES, toUserMessage } from '../../utils/userErrors';
import { Link } from 'react-router-dom';
import { CalendarDays, Gift, Megaphone, Percent, Target } from 'lucide-react';
import AppLoader from '../../components/modules/AppLoader';
import ModuleAlert from '../../components/modules/ModuleAlert';
import ModulePageHeader from '../../components/modules/ModulePageHeader';
import { getThemeColor } from '../../config/appConfig';
import apiService from '../../utils/apiService';
import { useFetchOnceByKey } from '../../hooks/useMountOnce';
import '../../styles/module-shared.css';
import {
  buildMyPromoStats,
  endsInLabel,
  formatMoney,
  formatPromoDate,
  normalizeMyPromotionList,
} from './promotionsData';
import './promotions.css';

const THEME = getThemeColor('primary');

const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;

const MyPromotions = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [mine, setMine] = useState([]);
  const [nowTick, setNowTick] = useState(Date.now());

  const fetchData = useCallback(async ({ cancelled }) => {
    setLoading(true);
    setError('');
    try {
      const res = await apiService.getMyAffiliatePromotions();
      if (cancelled()) return;
      if (!res.success) {
        setError(res.error || 'Failed to load my promotions');
        setMine([]);
      } else {
        setMine(normalizeMyPromotionList(res.data));
      }
    } catch (err) {
      if (!cancelled()) setError(toUserMessage(err, ERROR_MESSAGES.load));
    } finally {
      if (!cancelled()) setLoading(false);
    }
  }, []);

  useFetchOnceByKey('promotions-my', fetchData, true);

  useEffect(() => {
    if (!mine.some((p) => p.endDate && p.lifecycle === 'active')) return undefined;
    const id = setInterval(() => setNowTick(Date.now()), 60 * 1000);
    return () => clearInterval(id);
  }, [mine]);

  const stats = useMemo(() => buildMyPromoStats(mine), [mine]);

  const sortedMine = useMemo(() => {
    const rank = (p) => {
      if (p.statusKey === 'pending') return 0;
      if (p.statusKey === 'active') return 1;
      if (p.statusKey === 'approved') return 2;
      if (p.lifecycle === 'active') return 3;
      return 4;
    };
    return [...mine].sort((a, b) => {
      const byRank = rank(a) - rank(b);
      if (byRank) return byRank;
      return new Date(b.enrolledDate || 0) - new Date(a.enrolledDate || 0);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mine, nowTick]);

  return (
    <div className="page promo-page">
      <div className="page-inner">
        <ModulePageHeader
          icon={Megaphone}
          title="My Promotions"
          subtitle="Promotions you have joined or claimed."
          iconBg={`linear-gradient(135deg,${THEME},#a52a2a)`}
        />
        <ModuleAlert message={error} />

        {loading ? (
          <div className="module-card">
            <AppLoader color={THEME} />
          </div>
        ) : (
          <>
            <section className="promo-stats">
              <article className="promo-stat">
                {plural(stats.joined, 'Promotion joined', 'Promotions joined')}
              </article>
              <article className="promo-stat is-green">
                {plural(stats.active, 'Active now', 'Active now')}
              </article>
              <article className="promo-stat is-blue">
                {plural(stats.lotsTargetMet, 'Lots target met', 'Lots targets met')}
              </article>
              <article className="promo-stat is-red">
                {plural(stats.endingSoon, 'Ending within 7 days', 'Ending within 7 days')}
              </article>
            </section>

            <section className="promo-mine-grid">
              {sortedMine.length === 0 ? (
                <div className="promo-table-card" style={{ padding: '2rem', textAlign: 'center' }}>
                  <p className="promo-empty" style={{ margin: 0 }}>
                    You have not joined any promotions yet.
                  </p>
                  <Link
                    className="promo-link"
                    to="/promotions/list"
                    style={{ display: 'inline-block', marginTop: '0.75rem' }}
                  >
                    Browse promotions
                  </Link>
                </div>
              ) : (
                sortedMine.map((item) => (
                  <article key={item.id} className="promo-mine">
                    <div
                      className="promo-mine__hero"
                      style={{
                        background: `linear-gradient(180deg, ${item.tone}22 0%, #ffffff 100%)`,
                      }}
                    >
                      <div className="promo-mine__gift" style={{ background: item.tone }}>
                        <Gift className="w-6 h-6" />
                      </div>
                      <span className="promo-mine__badge">Your offer</span>
                    </div>
                    <div className="promo-mine__body">
                      <div className="promo-mine__title-row">
                        <h3>{item.title}</h3>
                        <span
                          className="promo-mine__pill"
                          style={{ background: item.statusTone || item.tone }}
                        >
                          {item.statusLabel}
                        </span>
                      </div>

                      {item.description ? (
                        <p className="promo-mine__desc">{item.description}</p>
                      ) : null}

                      <div className="promo-mine__bonus">
                        <div className="promo-mine__bonus-icon">
                          <Percent className="w-4 h-4" />
                        </div>
                        <div>
                          <span>{item.bonusHint || 'Bonus earned'}</span>
                          <strong>{item.bonusEarned}</strong>
                        </div>
                      </div>

                      <div className="promo-mine__ends">
                        <CalendarDays className="w-4 h-4" />
                        {item.lifecycle === 'active' && item.endDate
                          ? `Ends in ${endsInLabel(item.endDate)}`
                          : `Ends ${item.ends}`}
                      </div>

                      {item.enrolledDate ? (
                        <div className="promo-mine__ends">
                          <CalendarDays className="w-4 h-4" />
                          Joined {formatPromoDate(item.enrolledDate)}
                        </div>
                      ) : null}

                      <div className="promo-progress" style={{ marginTop: '0.75rem' }}>
                        <span>{item.progressLabel}</span>
                        <div className="promo-progress__bar">
                          <i
                            style={{
                              width: `${item.progressPct}%`,
                              background: `linear-gradient(90deg, ${item.tone}, ${item.statusTone || item.tone})`,
                            }}
                          />
                        </div>
                      </div>

                      <div className="promo-mine__meta">
                        {item.typeName ? <span>{item.typeName}</span> : null}
                        {item.minimumDeposit > 0 ? (
                          <span>Min deposit {formatMoney(item.minimumDeposit)}</span>
                        ) : null}
                        {item.requiredLots > 0 ? (
                          <span>
                            <Target
                              className="w-3.5 h-3.5"
                              style={{ display: 'inline', verticalAlign: '-2px' }}
                            />{' '}
                            {item.requiredLots} lots required
                          </span>
                        ) : null}
                      </div>

                      {item.ctaDisabled ? (
                        <button type="button" className="promo-mine__cta is-disabled" disabled>
                          {item.cta}
                        </button>
                      ) : (
                        <Link to="/promotions/list" className="promo-mine__cta">
                          {item.cta} →
                        </Link>
                      )}
                    </div>
                  </article>
                ))
              )}
            </section>
          </>
        )}
      </div>
    </div>
  );
};

export default MyPromotions;
